class FindInStoreDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#FindInStoreDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.storesMetaobjects = JSON.parse(this.getAttribute('data-storesMetaobjects'));
    this.storeList = this.querySelector('.store-list');
    this.sizeOptions = this.querySelectorAll('.size-option');

    // Initialize size option click handlers
    this.sizeOptions.forEach(option => {
      option.addEventListener('click', () => this.handleSizeChange(option));
    });

    // Initialize store accordions
    this.initStoreAccordions();
  }

  initStoreAccordions() {
    const storeItems = this.querySelectorAll('.store-item');
    
    storeItems.forEach(item => {
      const header = item.querySelector('.store-header');
      const content = item.querySelector('.store-content');
      const toggleBtn = item.querySelector('.toggle-btn');

      // Set initial state
      content.style.display = 'none';
      
      header.addEventListener('click', () => {
        const isExpanded = content.style.display === 'block';
        
        // Close all other store contents
        storeItems.forEach(otherItem => {
          if (otherItem !== item) {
            otherItem.querySelector('.store-content').style.display = 'none';
            otherItem.classList.remove('expanded');
          }
        });

        // Toggle current store content
        content.style.display = isExpanded ? 'none' : 'block';
        item.classList.toggle('expanded', !isExpanded);
      });
    });
  }

  async handleStockCheck() {
    try {
      const response = await fetch(`https://kivari.i8system.com/services/product/inventory.aspx?sku=${this.currentBarcode}`);
      const data = await response.json();
      
      // Merge store data with metaobjects
      const mergedStores = this.mergeStoreData(data.stores);
      
      // Render stores
      this.renderStores(mergedStores);
    } catch (error) {
      console.error('Error fetching stock data:', error);
      this.storeList.innerHTML = `
        <div class="store-list-error">
          <p>${window.FIND_IN_STORE_ERROR_MESSAGE}</p>
        </div>
      `;
    }
  }

  mergeStoreData(indigoStores) {
    // Filter and merge store data
    return this.storesMetaobjects?.sort((a, b) => a?.store_id > b?.store_id ? 1 : a?.store_id === b?.store_id ? 0 : -1)
      ?.map(metaStore => {
        const indigoStore = indigoStores.find(store => String(store.id) === String(metaStore.store_id));
        if (!indigoStore) return null;

        return {
          ...metaStore,
          quantity: indigoStore.quantity
        };
      })
      .filter(store => store !== null).sort((a, b) => a?.title?.localeCompare(b?.title));
  }

  renderStores(stores) {
    this.storeList.innerHTML = stores.map(store => `
      <div class="store-item" data-store-id="${store.store_id}">
        <div class="store-header">
          <div class="store-title">
            <h3>${store.title}</h3>
            <div class="stock-status ${this.getStockStatusClass(store.quantity)}">
              ${this.getStockStatusText(store.quantity)}
            </div>
            <div class="collect-info ${parseInt(store.quantity) >= window.SHOW_COLLECT_LEVEL ? 'available' : 'unavailable'}" store-quantity="${store.quantity}">
              <svg class="icon icon-tick" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="currentColor" stroke="#fff" d="m16.136 6.979.003-.004a1.499 1.499 0 0 0-2.275-1.954l-5.945 6.777-1.858-1.859A1.499 1.499 0 1 0 3.94 12.06l2.999 3c.289.29.675.44 1.1.439h.019a1.5 1.5 0 0 0 1.08-.522z"/></svg>
              Click & Collect Available
            </div>
            <a href="sms://${store.contact_number.replace(/\s+/g, "")};?body=${encodeURIComponent(`Hey, please confirm if ${this.selectedOption.dataset.productTitle} in ${this.selectedOption.dataset.productSize} is available at KIVARI ${store.title}`)}" class="store-contact">
              <svg width="16" height="16" viewBox="0 0 9 10" fill="none"><path fill="#262524" d="M4.5 1.4C2.476 1.4.9 2.885.9 4.624c0 .768.3 1.484.812 2.045l.147.16-.381 1.449L3 7.569l.156.057c.41.15.866.236 1.344.236 2.024 0 3.6-1.484 3.6-3.23C8.1 2.883 6.524 1.4 4.5 1.4ZM.1 4.624C.1 2.371 2.11.6 4.5.6c2.39 0 4.4 1.771 4.4 4.031S6.89 8.662 4.5 8.662a4.726 4.726 0 0 1-1.46-.23L.271 9.723.976 7.04A3.809 3.809 0 0 1 .1 4.624Z" /></g></svg>
              <span>Text us</span>
              <span class="underline">${store.contact_number}</span>
            </a>
          </div>
          <button class="toggle-btn" aria-label="Toggle store details">
            <span class="plus">+</span>
            <span class="minus">-</span>
          </button>
        </div>
        <div class="store-content">
          <a href="mailto:${store.email}" class="store-email">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#262524" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M22 6l-10 7L2 6" stroke="#262524" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            ${store.email}
          </a>
          ${store.address ? `
            <div class="store-address-text">
              ${store.address}
            </div>
            <div class="store-address">
              <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address)}" class="store-directions" target="_blank" rel="noopener">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" stroke="#262524" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Get Directions
              </a>
            </div>` : ''}
          ${(store.additional_details || store.additional_details_second_column) ? `
            <div class="store-details-container">
              ${store.additional_details ? `
                <div class="store-details store-details--first-column">
                  ${store.additional_details.replace(/\n/g, '<br>')}
                </div>
              ` : ''}
              ${store.additional_details_second_column ? `
                <div class="store-details store-details--second-column">
                  ${store.additional_details_second_column.replace(/\n/g, '<br>')}
                </div>
              ` : ''}
            </div>
          ` : ''}
        </div>
      </div>
    `).join('');

    // Reinitialize accordions after rendering
    this.initStoreAccordions();
  }

  getStockStatusClass(quantity) {
    if (quantity >= window.AVAILABLE_STOCK_LEVEL) return 'available';
    if (quantity <= window.LOW_STOCK_LEVEL && quantity > 0) return 'call-to-confirm';
    return 'sold-out';
  }

  getStockStatusText(quantity) {
    if (quantity >= window.AVAILABLE_STOCK_LEVEL) return 'AVAILABLE';
    if (quantity <= window.LOW_STOCK_LEVEL && quantity > 0) return 'LOW STOCK';
    return 'OUT OF STOCK';
  }

  handleSizeChange(selectedOption) {
    // Remove selected class from all options
    this.sizeOptions.forEach(option => option.classList.remove('selected'));
    selectedOption.classList.add('selected');

    // Store the selected option for SMS message
    this.selectedOption = selectedOption;

    // Update current barcode and fetch new stock data
    this.currentBarcode = selectedOption.dataset.barcode;
    this.handleStockCheck();
  }

  open() {
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    
    // Get current variant from cart store
    const currentVariant = Spruce.store('cartStore').current_variant;
    if (currentVariant) {
      // Find and select the size option matching the current variant's barcode
      const matchingOption = Array.from(this.sizeOptions).find(
        option => option.dataset.variantId === currentVariant
      );
      if (matchingOption) {
        this.handleSizeChange(matchingOption);
      }
    }
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('find-in-store-drawer', FindInStoreDrawer);


document.addEventListener('FIS-open', () => {
  document.querySelector('find-in-store-drawer').open();
});

document.addEventListener('FIS-close', () => {
  document.querySelector('find-in-store-drawer').close();
});


